import type { TFunction } from 'i18next';
import { orgThemeProposalErrorResponseSchema } from '@aica/api-types/schemas';
import { ApiError, apiErrorBody } from '@/lib/fetch-utils';
import { themeRefusalMessage } from './theme-error-message';

/**
 * What to say about a failed request of the weekly theme proposals (#845 PR 5b)
 * — by the refusal's `code`. Only the proposal-specific codes live here; the
 * codes shared with the theme set go through `themeRefusalMessage`, so
 * `feature_disabled` and the rest read the same as the set's writes. A bare 403
 * is the role, anything else the fallback («could not load» for the list,
 * «could not decide» next to a button).
 */
export async function proposalErrorMessage(
  error: unknown,
  t: TFunction,
  fallback: string
): Promise<string> {
  const raw = await apiErrorBody(error);
  const parsed = raw ? orgThemeProposalErrorResponseSchema.safeParse(raw) : null;
  // The proposals' own refusals are said here…
  if (parsed?.success) {
    switch (parsed.data.code) {
      case 'proposal_not_found':
        return t('dashboard:voicePage.themes.proposals.errors.notFound');
      case 'proposal_status_conflict':
        return t('dashboard:voicePage.themes.proposals.errors.statusConflict');
      case 'proposal_changed':
        return t('dashboard:voicePage.themes.proposals.errors.changed');
      case 'proposal_invalid':
        return fallback;
    }
  }
  // …everything shared with the set (full, flag, plan, narrow grant, bare role)
  // reads exactly as the set's own writes do.
  return themeRefusalMessage(
    raw,
    error instanceof ApiError ? error.status : undefined,
    t,
    { fallback, forbidden: t('dashboard:voicePage.themes.errors.forbidden') }
  );
}
